import { Divider, Input, Select, type SelectProps } from "antd";
import { Search } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";

import { useDict } from "@/hooks/useDict";

import styles from "./DictSelect.module.css";

/** 字典下拉 props：透传 Select，options 由 typeCode 自动注入 */
export interface DictSelectProps extends Omit<
  SelectProps,
  "options" | "showSearch" | "filterOption" | "onSearch" | "popupRender"
> {
  /** 字典类型编码（顶层 dict_code） */
  typeCode: string;
  /** 全部选项文本 */
  needAllText?: string;
  /** 是否需要全部选项 */
  needAll?: boolean;
  /** 下拉内是否展示搜索框，默认 true */
  searchable?: boolean;
  /** 搜索框 placeholder */
  searchPlaceholder?: string;
}

/**
 * 基于全局字典树的下拉选择器
 * 下拉面板内置搜索框与「仅启用」开关（默认开启），适用于筛选栏、表单等场景
 */
export const DictSelect = ({
  typeCode,
  placeholder = "请选择",
  loading,
  notFoundContent,
  needAllText,
  needAll,
  searchable = true,
  searchPlaceholder = "搜索...",
  onOpenChange,
  ...rest
}: DictSelectProps) => {
  const { getDictOptions, dictReady } = useDict();
  // 下拉内搜索关键字，关闭面板时清空
  const [searchKeyword, setSearchKeyword] = useState("");

  // 原始 options：可选注入「全部」项
  const allOptions = useMemo(() => {
    const dictOptions = getDictOptions(typeCode);
    if (needAll) {
      return [{ value: "all", label: needAllText || "全部" }, ...dictOptions];
    }
    return dictOptions;
  }, [getDictOptions, typeCode, needAllText, needAll]);

  // 按启用状态与关键字过滤后的 options
  const options = useMemo(() => {
    let list = allOptions;
    // 开关开启时隐藏禁用项；「全部」项始终保留
    const keyword = searchKeyword.trim().toLowerCase();
    if (searchable && keyword) {
      list = list.filter((item) => item.label.includes(keyword));
    }
    return list;
  }, [allOptions, searchKeyword, searchable]);

  // 字典加载中或未命中类型时给出友好提示
  const emptyContent = useMemo(() => {
    if (notFoundContent !== undefined) return notFoundContent;
    if (!dictReady) return "字典加载中…";
    if (!allOptions.length) return "暂无字典项";
    if (!options.length) return "无匹配项";
    return undefined;
  }, [dictReady, notFoundContent, allOptions.length, options.length]);

  /** 下拉面板：搜索框 + 仅启用开关 + 选项列表 */
  const popupRender = (menu: ReactElement) => {
    if (!searchable) return menu;
    return (
      <div onMouseDown={(e) => e.preventDefault()}>
        {searchable && (
          <div className={styles.popupSearch}>
            <Input
              allowClear
              value={searchKeyword}
              placeholder={searchPlaceholder}
              prefix={<Search className="w-3.5 h-3.5 text-slate-400" />}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        )}
        {searchable && <Divider style={{ margin: "4px 0" }} />}
        {menu}
      </div>
    );
  };

  /** 关闭下拉时重置搜索关键字 */
  const handleOpenChange = (open: boolean) => {
    if (!open) setSearchKeyword("");
    onOpenChange?.(open);
  };

  return (
    <Select
      allowClear
      placeholder={placeholder}
      options={options}
      loading={loading ?? !dictReady}
      notFoundContent={emptyContent}
      popupRender={popupRender}
      onOpenChange={handleOpenChange}
      {...rest}
    />
  );
};
